import {useEffect, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import Switch from "@mui/material/Switch";
import SoftInput from "components/SoftInput";
import logo from "assets/images/logo.png";
import background from "assets/images/curved-images/background.png";
import {login} from "../../../axios-client";
import {useSoftUIController} from "../../../context";
import {setLayout, setToken, setUserInfo} from "../../../store/actions/app-actions";
import Alert from "@mui/material/Alert";
import {FREE_AGENT} from "../../../utlis/constant";
import { useTheme } from "@mui/material/styles";
import {
    Grid,
    Box,
    Typography,
    Button,
    IconButton,
} from "@mui/material";
import { ArrowRight, ArrowLeft, Eye, EyeOff } from "lucide-react";
import Card from "@mui/material/Card";
import LanguageSwitcher from "../../../reusableComponents/LanguageSwitcher/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import Footer from "../components/Footer";

import {
    Checkbox,
    FormControlLabel,
    InputAdornment,
    Container,
    Stack,
    Divider,
    CardContent
} from "@mui/material"

function SignIn() {
    const theme = useTheme();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [controller, dispatch] = useSoftUIController();
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const navigate = useNavigate();
    const [animateLogo, setAnimateLogo] = useState(false);


    const {i18n} = useTranslation();
    const { t } = useTranslation(['signIn']);
    const dir = i18n.dir();
    const currentLang = i18n.language;
    const features = t('panel.features', { returnObjects: true });
    const signUpPage = `/${currentLang}/authentication/sign-up`;

    useEffect(() => {
        setTimeout(() => setAnimateLogo(true), 300);
        setErrorMessage(null);
    }, [email, password]);


    useEffect(() => {
        setLayout(dispatch, "page");
    }, []);

    const handleLogin = (event) => {
        event.preventDefault();
        setIsLoading(true);
        login(email, password, FREE_AGENT)
            .then((response) => {
                setUserInfo(dispatch, response?.data);
                setToken(dispatch, response.data?.access_token);
                localStorage.setItem("token", response.data?.access_token);
                navigate(`/${currentLang}/documents`);
            })
            .catch((error) => {
                if (error.response && error.response.data && error.response.data.message) {
                    setErrorMessage(error.response.data.message);
                } else {
                    setErrorMessage('An error occurred. Please try again later.');
                }
            })
            .finally(() => setIsLoading(false));
    };


    return (
        <Box
            sx={{
                minHeight: "100vh",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
            }}
        >
            {/* Background Image with Brightness Filter */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `url(${background})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    filter: "brightness(1.8) contrast(1.2) saturate(1.1)",
                }}
            />

            {/* Light Overlay to Further Brighten */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                }}
            />

            <Box
                sx={{
                    position: "absolute",
                    top: theme.spacing(2),
                    left: theme.spacing(2),
                    right: theme.spacing(2),
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    zIndex: 10,
                }}
            >
                <Box
                    sx={{
                        opacity: animateLogo ? 1 : 0,
                        transform: animateLogo ? "translateY(0)" : "translateY(-10px)",
                        transition: "all 0.8s ease",
                    }}

                >
                    <img src={logo} alt="YaiGlobal Logo" style={{ height: 48 }} />
                </Box>

                <LanguageSwitcher color={"white"} />
            </Box>

            <Box
                sx={{
                    position: "relative",
                    zIndex: 10,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                    maxWidth: 400,
                }}
            >

            {/* Login Form */}
            <Card
                sx={{
                    position: "relative",
                    zIndex: 10,
                    width: "100%",
                    maxWidth: 400,
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(8px)",
                    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                    border: "none",
                }}
            >
                <CardContent sx={{ p: 4 }}>
                    {/* Header */}
                    <Box sx={{ textAlign: "center", mb: 3 }}>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{
                                fontWeight: "bold",
                                color: "#111827",
                                mb: 1,
                            }}
                        >
                            {t("login.welcome")}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#6B7280",
                            }}
                        >
                            {t("login.description")}
                        </Typography>
                    </Box>


                    {errorMessage && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
                            {t("login.errors.invalidCredentials")}
                        </Alert>
                    )}

                    {/* Form */}

                    <Box component="form" onSubmit={handleLogin} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                        <SoftInput
                            type="email"
                            placeholder={t("login.email")}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />

                        <Box sx={{ position: "relative" }}>

                            <SoftInput
                                type={showPassword ? "text" : "password"}
                                placeholder={t("login.password")}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                sx={{
                                    position: "absolute",
                                    ...(i18n.language === "ar" ? {left:8} : {right:8}),
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    padding: 0,
                                    color: "#6b7280",
                                }}
                            >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </IconButton>
                        </Box>

                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Switch
                                    checked={rememberMe}
                                    onChange={() => setRememberMe(!rememberMe)}
                                    sx={{
                                        color: "#d946ef",
                                        "&.Mui-checked": { color: "#d946ef" },
                                        "&.Mui-checked + .MuiSwitch-track": {
                                            backgroundColor: "#d946ef",
                                        },
                                    }}
                                />
                                <Typography variant="caption" sx={{ ml: 1, marginRight: "5px"}}>
                                    {t("login.rememberMe")}
                                </Typography>
                            </Box>

                            <Typography
                                variant="caption"
                                sx={{ color: "#d946ef", cursor: "pointer" }}
                                onClick={() => navigate(`/${currentLang}/authentication/reset-password`)}
                            >
                                {t("login.forgotPassword")}
                            </Typography>
                        </Box>

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={isLoading}
                            sx={{
                                height: 45,
                                background: "linear-gradient(90deg, #d946ef, #ec4899)",
                                color: "#fff",
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: 2,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #b91cdb, #d946ef)",
                                },
                            }}
                            endIcon={
                                <Box component="span" sx={{ ml: dir === "ltr" ? 1 : 0, mr: dir === "rtl" ? 2 : 0 }}>
                                    {dir === "rtl" ? <ArrowLeft size={16} /> : <ArrowRight size={16} />}
                                </Box>
                            }
                        >
                            {isLoading ? t("login.signingIn") : t("login.signIn")}
                        </Button>
                        <Typography variant="caption" align="center" sx={{ mt: 2 }}>
                            {t("login.noAccount")}{" "}
                            <Link to={signUpPage} style={{ color: "#d946ef", textDecoration: "none", fontWeight: 500 }}>
                                {t("login.signUp")}
                            </Link>
                        </Typography>
                    </Box>
                </CardContent>

            </Card>
            <Footer color="white" />
        </Box>
        </Box>


    )
}

export default SignIn;
